import { SecretContent, SecretCreateResponse, SecretMetadata, SecretOptions, ServerConfig } from '../types';

const BASE_URL = '/api'; // Changed to absolute path

const bufferToBase64 = (buffer: ArrayBuffer) => {
  return btoa(
    new Uint8Array(buffer).reduce((data, byte) => data + String.fromCharCode(byte), '')
  );
}

const base64ToBuffer = (base64: string) => {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for(let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
}

/**
 * Derives a key from PIN and salt using PBKDF2
 */
const deriveKeyFromPIN = async (pin: string, salt: Uint8Array, iterations: number = 600000, extractable: boolean = false): Promise<CryptoKey> => {
  const encoder = new TextEncoder();
  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    encoder.encode(pin),
    'PBKDF2',
    false,
    ['deriveKey']
  );
  
  return await crypto.subtle.deriveKey(
    { name: "PBKDF2", salt, iterations, hash: "SHA-256" },
    keyMaterial,
    { name: "AES-GCM", length: 256 },
    extractable,  // CHANGED: Now configurable
    ["encrypt", "decrypt"]
  );
};

/**
 * Creates SHA-256 hash of input
 */
const sha256 = async (data: Uint8Array): Promise<Uint8Array> => {
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  return new Uint8Array(hashBuffer);
};

/**
 * Plaintext frame: [header length: uint32 BE][header: UTF-8 JSON][body: raw bytes]
 * The body is the UTF-8 text or the file bytes, so nothing is base64-encoded.
 */
interface FrameHeader {
  type: 'text' | 'file'
  filename?: string
  mimeType?: string
}

const encodeFrame = (header: FrameHeader, body: Uint8Array): Uint8Array => {
  const headerBytes = new TextEncoder().encode(JSON.stringify(header));
  const frame = new Uint8Array(4 + headerBytes.length + body.length);
  new DataView(frame.buffer).setUint32(0, headerBytes.length);
  frame.set(headerBytes, 4);
  frame.set(body, 4 + headerBytes.length);
  return frame;
};

const decodeFrame = (frame: ArrayBuffer): { header: FrameHeader, body: ArrayBuffer } => {
  const headerLength = new DataView(frame).getUint32(0);
  const header = JSON.parse(new TextDecoder().decode(new Uint8Array(frame, 4, headerLength)));
  return { header, body: frame.slice(4 + headerLength) };
};

/**
 * Error for a failed unlock. `deleted` means the secret is gone because of too
 * many wrong PINs; `remainingAttempts` is set for a wrong PIN.
 * (Plain Error with extra fields: the ES5 target breaks instanceof on subclasses.)
 */
export type UnlockError = Error & { deleted?: boolean, remainingAttempts?: number };

const unlockError = (message: string, fields: { deleted?: boolean, remainingAttempts?: number }): UnlockError =>
  Object.assign(new Error(message), fields);

// One-time secrets are gone after viewing; say so without telling which case applies
export const NOT_FOUND_MESSAGE = 'Dieser Link wurde bereits angezeigt, ist abgelaufen oder nicht korrekt.';

export const api = {
  /**
   * POST /api/secrets (body: IV || ciphertext, headers: X-Verification-Key, X-Expires-In, X-One-Time)
   * New encryption scheme:
   * - Generate random S1
   * - Derive S2 = SHA256(S1)
   * - Derive K1 = PBKDF2(PIN, S1) - for encrypting the actual secret
   * - Derive K2 = PBKDF2(PIN, S2) - for server verification
   * - Encrypt content with K1
   * - Send K2 and encrypted content to server
   * - Return URL with S1 in fragment
   */
  createSecret: async (content: string | File, pin: string, options: SecretOptions): Promise<SecretCreateResponse> => {
    const plaintext = content instanceof File
      ? encodeFrame(
          { type: 'file', filename: content.name, mimeType: content.type },
          new Uint8Array(await content.arrayBuffer())
        )
      : encodeFrame({ type: 'text' }, new TextEncoder().encode(content));
    
    // Step 1: Generate random salt S1
    const S1 = crypto.getRandomValues(new Uint8Array(32));
    
    // Step 2: Derive S2 = SHA256(S1)
    const S2 = await sha256(S1);
    
    // Step 3: Derive K1 = PBKDF2(PIN, S1) - for encrypting content
    const K1 = await deriveKeyFromPIN(pin, S1, 600000, false); // Not extractable
    
    // Step 4: Derive K2 = PBKDF2(PIN, S2) - for server verification
    const K2 = await deriveKeyFromPIN(pin, S2, 600000, true); // MUST be extractable
    
    // Step 5: Encrypt the secret content with K1
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const encryptedContent = await crypto.subtle.encrypt(
      { name: 'AES-GCM', iv },
      K1,
      plaintext
    );
    
    // Step 6: Prepend IV to ciphertext (IV || ciphertext)
    const combined = new Uint8Array(iv.length + encryptedContent.byteLength);
    combined.set(iv, 0);
    combined.set(new Uint8Array(encryptedContent), iv.length);
    
    // Step 7: Export K2 as raw bytes to send to server
    const K2_raw = await crypto.subtle.exportKey('raw', K2);
    
    // Step 8: Send IV || C as raw bytes, K2 in a header
    const response = await fetch(`${BASE_URL}/secrets`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/octet-stream',
        'X-Verification-Key': bufferToBase64(K2_raw),
        'X-Expires-In': String(options.expiresIn),
        'X-One-Time': String(options.oneTime),
      },
      body: combined,
    });
    
    if (!response.ok) {
      const { msg } = await response.json().catch(() => ({ msg: undefined }));
      throw new Error(msg || 'Failed to create secret');
    }
    
    const responseData = await response.json();
    
    // Step 9: Construct URL with S1 in fragment
    // Using BrowserRouter, so URL format is: /secret/{id}#{s1}
    const baseUrl = `${window.location.origin}/secret/${responseData.id}`;
    const urlWithFragment = `${baseUrl}#${bufferToBase64(S1.buffer)}`;
    
    return {
      ...responseData,
      url: urlWithFragment
    };
  },

  /**
   * GET /api/config
   * Allowed and default lifetimes for new secrets
   */
  getConfig: async (): Promise<ServerConfig> => {
    const response = await fetch(`${BASE_URL}/config`);
    if (!response.ok) throw new Error('Failed to fetch config');
    return response.json();
  },

  /**
   * GET /api/secrets/:id/metadata
   * Check if secret exists without needing PIN. Read-only: never consumes a
   * one-time secret, so link scanners opening the page cannot burn it.
   */
  getSecretMetadata: async (id: string): Promise<SecretMetadata> => {
    const response = await fetch(`${BASE_URL}/secrets/${id}/metadata`);
    if (!response.ok) {
      if (response.status === 404) {
        return { exists: false };
      }
      throw new Error('Failed to fetch secret metadata');
    }
    return response.json();
  },

  /**
   * POST /api/secrets/:id/verify
   * Verify PIN and retrieve encrypted secret
   * New flow:
   * - Extract S1 from URL fragment
   * - Derive S2 = SHA256(S1)
   * - Derive K2 = PBKDF2(PIN, S2)
   * - Send K2 to server for verification
   * - Server returns encrypted content if K2 matches
   */
  verifyAndGetSecret: async (id: string, pin: string, s1Base64: string): Promise<ArrayBuffer> => {
    // Step 1: Decode S1 from URL fragment
    const S1 = new Uint8Array(base64ToBuffer(s1Base64));
    
    // Step 2: Derive S2 = SHA256(S1)
    const S2 = await sha256(S1);
    
    // Step 3: Derive K2 = PBKDF2(PIN, S2)
    const K2 = await deriveKeyFromPIN(pin, S2, 600000, true);
    
    // Step 4: Export K2 to send to server
    const K2_raw = await crypto.subtle.exportKey('raw', K2);
    
    // Step 5: Send K2 to server for verification
    const response = await fetch(`${BASE_URL}/secrets/${id}/verify`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        verificationKey: bufferToBase64(K2_raw)
      })
    });
    
    if (!response.ok) {
      if (response.status === 401) {
        const { remainingAttempts } = await response.json();
        throw unlockError('Falscher PIN!', { remainingAttempts });
      }
      if (response.status === 410) {
        throw unlockError('Zu viele Fehlversuche. Das Geheimnis wurde gelöscht.', { deleted: true });
      }
      if (response.status === 404) {
        throw unlockError(NOT_FOUND_MESSAGE, { deleted: true });
      }
      throw new Error('Failed to verify PIN');
    }
    
    return response.arrayBuffer(); // IV || ciphertext
  },

  /**
   * Decrypt the secret content using PIN and S1
   */
  decryptSecret: async (encrypted: ArrayBuffer, pin: string, s1Base64: string): Promise<SecretContent> => {
    // Step 1: Decode S1 from URL fragment
    const S1 = new Uint8Array(base64ToBuffer(s1Base64));
    
    // Step 2: Derive K1 = PBKDF2(PIN, S1)
    const K1 = await deriveKeyFromPIN(pin, S1);
    
    // Step 3: Split IV (first 12 bytes) from ciphertext
    const iv = new Uint8Array(encrypted, 0, 12);
    const ciphertext = new Uint8Array(encrypted, 12);
    
    // Step 4: Decrypt content with K1
    const decrypted = await crypto.subtle.decrypt(
      { name: "AES-GCM", iv },
      K1,
      ciphertext
    );
    
    const { header, body } = decodeFrame(decrypted);

    if (header.type === 'text') {
      return { type: 'text', text: new TextDecoder().decode(body) };
    }
    return {
      type: 'file',
      content: body,
      metadata: {
        filename: header.filename || 'download',
        mimeType: header.mimeType || 'application/octet-stream',
        size: body.byteLength,
      },
    };
  }
};
